/** The way from a register to the record page of what it lists — **F-007**.
 *
 * `/records/{id}` answers for every kind of record (EXP-15), and the work that hangs off one —
 * tasks, notes, flags and **approvals** — lives only there. But a grep of `/records/` found a link
 * for activity records and nothing for the other kinds, so a risk, an initiative, an expected
 * source, a document and a filing each had a page nobody could reach without typing an address. The
 * consequence that mattered: **there was no clickable path to submit a filing for approval.**
 *
 * **One component, because the destination is one fact.** Five registers link to the same page with
 * the same search, and five copies of a `Link` are five chances for one to drop the reporting window
 * (`keepSearch` is what carries it) or to forget the label that tells a screen reader *which* risk.
 *
 * **The label names the work, not the page.** "Notes, flags and approvals" is what is behind the
 * link; "Record" says only where it goes (design rule 7). The accessible name appends the subject,
 * because a list of twelve identical links is a list nobody can navigate by link.
 *
 * `data-record-kind` and `data-record-id` are the structural claim, so a test or the browser gate
 * asserts *what the link points at* rather than what it says.
 */

import { Link } from "@tanstack/react-router";

import type { RowMenuItem } from "../components/ui";

import type { ScreenSearch } from "./search";
import { keepSearch } from "./search";
import reg from "./Register.module.css";
import styles from "./Screen.module.css";

export function RecordLink({
  recordId,
  kind,
  subject,
  tab,
}: {
  /** The underlying row's own id — `record.id` **is** the row's id (ADR-0037), so every register
   * already holds the one this needs. */
  recordId: string;
  /** The `RecordKind` the server registered this under. Asserted, not used to route: the page
   * resolves the kind itself, and a client that chose the destination by kind would be a second
   * place deciding what a record is. */
  kind: string;
  /** What the link is about, for the accessible name: a risk's title, a site and a month. */
  subject: string;
  /** Which tab to land on. Omitted, the record opens on its own first tab. A register that wants the
   * *work* (EXP-06's workflow destination) passes `workflow`, so the reader does not arrive on
   * Review and have to find the tab. */
  tab?: string;
}) {
  return (
    <Link
      className={[reg.act, styles.recordLink].join(" ")}
      to="/records/$recordId"
      params={{ recordId }}
      search={tab ? (prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab }) : keepSearch}
      aria-label={`Notes, flags and approvals for ${subject}`}
      data-testid="record-link"
      data-record-kind={kind}
      data-record-id={recordId}
    >
      Notes, flags and approvals ›
    </Link>
  );
}

/** The same link as a menu item of a table row (`RowMenu`): the destination is one fact, so it is the same
 * link with the same data attributes, drawn inside a menu where a column of its own held 172px of every row
 * open (U6, 2026-11-03). */
export function recordMenuItem({
  recordId,
  kind,
  subject,
}: {
  recordId: string;
  kind: string;
  subject: string;
}): RowMenuItem {
  return {
    label: "Notes, flags and approvals",
    render: ({ className, close }) => (
      <Link
        role="menuitem"
        className={className}
        to="/records/$recordId"
        params={{ recordId }}
        search={keepSearch}
        aria-label={`Notes, flags and approvals for ${subject}`}
        data-testid="record-link"
        data-record-kind={kind}
        data-record-id={recordId}
        onClick={close}
      >
        Notes, flags and approvals
      </Link>
    ),
  };
}
